import { useState } from "react";
import { pastorBenefits, memberBenefits, images } from "../../data/mock";
import { Check, Shield, Users, Send, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";

export const BenefitsSection = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    iglesia: "",
    mensaje: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.telefono || !formData.iglesia) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }
    setIsSubmitting(true);
    // Simulate API call - save to localStorage
    setTimeout(() => {
      const existing = JSON.parse(localStorage.getItem("contactRequests") || "[]");
      existing.push({ ...formData, timestamp: new Date().toISOString() });
      localStorage.setItem("contactRequests", JSON.stringify(existing));
      setIsSubmitting(false);
      setSubmitted(true);
      toast.success("Solicitud enviada. Nuestro equipo te contactará pronto.");
      setFormData({ nombre: "", email: "", telefono: "", iglesia: "", mensaje: "" });
    }, 1200);
  };

  return (
    <>
      {/* Pastors Section */}
      <section id="pastors" className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-brand-950" />
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-15">
          <img
            src={images.congregation}
            alt="Congregación"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-950 via-brand-950/80 to-brand-950/40" />
        </div>
        <div className="absolute top-20 left-20 w-64 h-64 bg-brand-light/8 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-40 w-48 h-48 bg-brand-500/6 rounded-full blur-3xl animate-float" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-light/10 border border-brand-light/20 mb-6">
              <Shield className="w-4 h-4 text-brand-light" />
              <span className="text-brand-300 text-sm font-semibold">Para Líderes</span>
            </div>

            <h2 className="brand-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Pastores y Ministros
            </h2>
            <p className="text-white/50 text-lg mb-10 leading-relaxed">
              Herramientas diseñadas para potenciar tu liderazgo ministerial.
              Todo lo que necesitas para gestionar y expandir tu congregación en un solo lugar.
            </p>

            <div className="space-y-4 mb-10">
              {pastorBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-7 h-7 rounded-lg bg-brand-light/15 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-brand-light/25 group-hover:scale-110 transition-all duration-300">
                    <Check className="w-4 h-4 text-brand-light" />
                  </div>
                  <span className="text-white/60 font-medium group-hover:text-white/85 transition-colors duration-300">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Button for Pastors */}
            <Button
              onClick={() => {
                const el = document.querySelector("#contact-form");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-brand-light hover:bg-brand-400 text-brand-950 px-8 py-6 text-lg rounded-2xl font-bold transition-all duration-300 hover:scale-105 shadow-xl shadow-brand-light/20 gap-2"
            >
              Solicita tu Demo
              <ArrowRight className="w-5 h-5" />
            </Button>

            <div className="mt-10 p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-brand-light/8">
              <p className="text-white/35 italic text-sm leading-relaxed">
                "Por tanto, mirad por vosotros, y por todo el rebaño en que el Espíritu Santo os ha puesto por obispos, para apacentar la iglesia del Señor."
              </p>
              <p className="text-brand-light/40 text-xs mt-2 font-bold">Hechos 20:28</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section for Pastors */}
      <section id="contact-form" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-900 to-brand-950" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-brand-light/5 rounded-full blur-3xl" />

        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'radial-gradient(circle, #00CCFF 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-5 py-2 rounded-full bg-brand-light/10 border border-brand-light/15 text-brand-light text-xs font-bold uppercase tracking-widest mb-5 brand-heading">
              Contacto
            </span>
            <h2 className="brand-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Agenda una demo con nuestro equipo
            </h2>
            <p className="text-brand-200/40 text-base max-w-xl mx-auto">
              Completa el formulario y un miembro de nuestro equipo te contactará para mostrarte cómo Tu Iglesia App puede transformar tu ministerio.
            </p>
          </div>

          {submitted ? (
            <div className="text-center p-12 rounded-3xl bg-white/5 border border-brand-light/20 animate-scale-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand-light/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-brand-light" />
              </div>
              <h3 className="brand-heading text-2xl font-bold text-white mb-3">Solicitud Recibida</h3>
              <p className="text-white/50 max-w-md mx-auto mb-6">
                Gracias por tu interés. Un miembro de nuestro equipo se comunicará contigo a la brevedad.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="border-brand-light/30 text-brand-light hover:bg-brand-light/10 rounded-xl"
              >
                Enviar otra solicitud
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 md:p-10 rounded-3xl bg-white/[0.04] backdrop-blur-sm border border-brand-light/10 hover:border-brand-light/20 transition-colors duration-500">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-white/70 text-sm font-semibold">
                    Nombre completo *
                  </Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    className="bg-white/5 border-brand-light/15 text-white placeholder:text-white/25 rounded-xl h-12 focus:border-brand-light/40 focus:ring-brand-light/20 transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70 text-sm font-semibold">
                    Correo electrónico *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="correo@ejemplo.com"
                    className="bg-white/5 border-brand-light/15 text-white placeholder:text-white/25 rounded-xl h-12 focus:border-brand-light/40 focus:ring-brand-light/20 transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-white/70 text-sm font-semibold">
                    Teléfono *
                  </Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+1 (000) 000-0000"
                    className="bg-white/5 border-brand-light/15 text-white placeholder:text-white/25 rounded-xl h-12 focus:border-brand-light/40 focus:ring-brand-light/20 transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iglesia" className="text-white/70 text-sm font-semibold">
                    Iglesia / Ministerio *
                  </Label>
                  <Input
                    id="iglesia"
                    name="iglesia"
                    value={formData.iglesia}
                    onChange={handleChange}
                    placeholder="Nombre de tu iglesia"
                    className="bg-white/5 border-brand-light/15 text-white placeholder:text-white/25 rounded-xl h-12 focus:border-brand-light/40 focus:ring-brand-light/20 transition-colors"
                  />
                </div>
              </div>
              <div className="space-y-2 mb-8">
                <Label htmlFor="mensaje" className="text-white/70 text-sm font-semibold">
                  Mensaje (opcional)
                </Label>
                <Textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Cuéntanos sobre tu ministerio..."
                  rows={4}
                  className="bg-white/5 border-brand-light/15 text-white placeholder:text-white/25 rounded-xl focus:border-brand-light/40 focus:ring-brand-light/20 transition-colors resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-light hover:bg-brand-400 text-brand-950 py-6 text-lg rounded-2xl font-bold transition-all duration-300 hover:scale-[1.02] shadow-xl shadow-brand-light/20 gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-brand-950/30 border-t-brand-950 rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Quiero ser Contactado
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Members Section */}
      <section id="members" className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#f0f9ff]" />
        <div className="absolute left-0 top-0 w-1/3 h-full opacity-8">
          <img
            src={images.community}
            alt="Comunidad"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#f0f9ff] via-[#f0f9ff]/80 to-[#f0f9ff]/40" />
        </div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-brand-200/30 rounded-full blur-3xl" />
        <div className="absolute top-20 right-40 w-48 h-48 bg-brand-100/40 rounded-full blur-3xl animate-float" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl ml-auto text-right">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-100 border border-brand-200 mb-6">
              <Users className="w-4 h-4 text-brand-700" />
              <span className="text-brand-700 text-sm font-semibold">Para la Congregación</span>
            </div>

            <h2 className="brand-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Para Cada Miembro
            </h2>
            <p className="text-slate-500 text-lg mb-10 leading-relaxed">
              Tu iglesia siempre contigo. Conéctate, crece espiritualmente y participa
              activamente desde cualquier lugar y en cualquier momento.
            </p>

            <div className="space-y-4">
              {memberBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 justify-end group"
                >
                  <span className="text-slate-600 font-medium group-hover:text-slate-800 transition-colors duration-300">
                    {benefit}
                  </span>
                  <div className="w-7 h-7 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-brand-200 group-hover:scale-110 transition-all duration-300">
                    <Check className="w-4 h-4 text-brand-700" />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-2xl bg-white border border-brand-100 shadow-sm text-left">
              <p className="text-slate-400 italic text-sm leading-relaxed">
                "Y todos los días, en el templo y por las casas, no cesaban de enseñar y predicar a Jesucristo."
              </p>
              <p className="text-brand-700/50 text-xs mt-2 font-bold">Hechos 5:42</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
